import { Link } from "react-router-dom";
import { ChevronDown, Menu, ArrowRight, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { BetaTopStrip } from "@/components/marketing/BetaTopStrip";
import { FEATURE_GROUPS, featurePagePath, groupedFeaturePages } from "@/lib/featurePages";

/** Shared five-column feature grid used by both the mega menu and mobile sheet. */
const FeatureMenuPanel = ({ onNavigate }: { onNavigate?: () => void }) => {
  const groups = groupedFeaturePages();
  return (
    <div className="grid gap-x-6 gap-y-7 px-4 py-6 sm:grid-cols-2 sm:px-6 lg:grid-cols-5">
      {FEATURE_GROUPS.map((g) => {
        const pages = groups[g.key];
        if (!pages || pages.length === 0) return null;
        const isSoon = g.key === "comingSoon";
        return (
          <div key={g.key}>
            <div className="mkt-eyebrow mb-3 text-primary">{g.label}</div>
            <ul className="space-y-2.5">
              {pages.map((p) => {
                const Icon = p.blocks[0]?.icon;
                return (
                  <li key={p.slug}>
                    <Link
                      to={featurePagePath(p.slug)}
                      onClick={onNavigate}
                      className="flex gap-2.5 rounded-sm p-1.5 transition-colors hover:bg-primary/10"
                    >
                      {Icon ? (
                        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                      ) : null}
                      <span className="min-w-0">
                        <span className="block text-[13px] leading-snug text-foreground">
                          {p.navLabel}
                          {isSoon ? (
                            <span className="ml-1.5 rounded-[2px] border border-border px-1 py-px align-middle text-[9px] uppercase tracking-wide text-muted-foreground">
                              Soon
                            </span>
                          ) : null}
                        </span>
                        <span className="mt-0.5 block text-[12px] leading-relaxed text-muted-foreground">
                          {p.navDescription}
                        </span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Full-width bordered marketing header with a full-width mega menu, matching
 * the Secretarius site chrome in the Ink & Indigo palette.
 */
export const MarketingNav = () => {
  const { user, loading } = useAuth();
  const signedIn = !!user;
  const [features, setFeatures] = useState(false);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    if (!features) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFeatures(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [features]);

  return (
    <>
      <BetaTopStrip />
      <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur">
        <div
          className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6"
          onMouseLeave={() => setFeatures(false)}
        >
          <Link to="/" className="flex items-center gap-2">
            <span
              role="img"
              aria-label="Excerptus logo"
              className="inline-block h-7 w-7 bg-primary"
              style={{
                WebkitMaskImage: "url(/excerptus-logo.webp)",
                maskImage: "url(/excerptus-logo.webp)",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskSize: "contain",
                maskSize: "contain",
                WebkitMaskPosition: "center",
                maskPosition: "center",
              }}
            />
            <span className="font-serif text-xl font-semibold text-primary sm:text-2xl">
              Excerptus
            </span>
          </Link>

          <div className="flex items-center gap-2">
            <nav className="hidden items-center gap-1 md:flex">
              <button
                type="button"
                aria-expanded={features}
                onClick={() => setFeatures((v) => !v)}
                onMouseEnter={() => setFeatures(true)}
                className="inline-flex items-center gap-1 rounded-sm px-3 py-1.5 text-[13px] text-muted-foreground transition-colors hover:bg-primary/10 hover:text-foreground aria-expanded:bg-primary/10 aria-expanded:text-foreground"
              >
                Features
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${features ? "rotate-180" : ""}`}
                  aria-hidden
                />
              </button>
              <Link
                to="/pricing/"
                onMouseEnter={() => setFeatures(false)}
                className="rounded-sm px-3 py-1.5 text-[13px] text-muted-foreground transition-colors hover:bg-primary/10 hover:text-foreground"
              >
                Pricing
              </Link>
            </nav>

            <div className={loading ? "pointer-events-none opacity-0" : ""}>
              {signedIn ? (
                <Link to="/dashboard">
                  <Button size="sm">Dashboard</Button>
                </Link>
              ) : (
                <div className="flex items-center gap-1 sm:gap-2">
                  <Link to="/auth/" className="hidden sm:inline-flex">
                    <Button variant="ghost" size="sm">Sign in</Button>
                  </Link>
                  <Link to="/pricing/">
                    <Button size="sm">Get started</Button>
                  </Link>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setMobile((v) => !v)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-sm border border-border text-foreground md:hidden"
              aria-label="Toggle navigation"
            >
              {mobile ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Desktop mega menu — full-bleed band under the header */}
        {features ? (
          <div
            className="hidden border-t border-border bg-background md:block"
            onMouseEnter={() => setFeatures(true)}
            onMouseLeave={() => setFeatures(false)}
          >
            <div className="mx-auto max-w-6xl">
              <FeatureMenuPanel onNavigate={() => setFeatures(false)} />
              <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3 text-[12px] sm:px-6">
                <Link
                  to="/pricing/"
                  onClick={() => setFeatures(false)}
                  className="inline-flex items-center gap-1.5 text-primary hover:underline"
                >
                  See every feature on the pricing page
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                </Link>
              </div>
            </div>
          </div>
        ) : null}

        {/* Mobile panel */}
        {mobile ? (
          <div className="max-h-[80vh] overflow-y-auto border-t border-border bg-background md:hidden">
            <FeatureMenuPanel onNavigate={() => setMobile(false)} />
            <div className="flex flex-col gap-2 border-t border-border px-4 py-4 text-sm sm:px-6">
              <Link to="/pricing/" onClick={() => setMobile(false)} className="text-foreground">
                Pricing
              </Link>
              {!signedIn && (
                <Link to="/auth/" onClick={() => setMobile(false)} className="text-muted-foreground">
                  Sign in
                </Link>
              )}
            </div>
          </div>
        ) : null}
      </header>
    </>
  );
};
